import * as THREE from 'three'
import ReactDOM from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import { Canvas, useFrame, extend, useThree } from '@react-three/fiber'
import { OrbitControls, Instances, Instance } from '@react-three/drei'
import allColors from './Colors'
import { SimpleReverb } from './SimpleReverb'
import { LayerMaterial, Color, Depth, Noise } from 'lamina/vanilla'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import './styles.css'

let damp = 0
let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
const ci1 = randI(allColors.length)
const ci2 = randI(allColors.length)
const ci3 = randI(allColors.length)
const col1 = allColors[ci1]
const col2 = allColors[ci2]
const col3 = allColors[ci3]
let currCol = []
currCol.push(...col1)
if(randI(10)>5){
  currCol.push(...col2)
}
if(randI(10)>5){
  currCol.push(...col3)
}
let bg = "#000"

const material = new LayerMaterial({
  depthWrite: false,
  layers: [
    new Color({ color: '#fff' }),
    new Noise({ mapping: 'local', type: 'simplex', scale: 10000000000, colorA: 'black', colorB: 'black', mode: 'subtract' })
  ]
})
const material2 = new LayerMaterial({
  depthTest: false,
  layers: [
    new Color({ color: rand(currCol) }),
    new Noise({ mapping: 'local', type: 'simplex', scale: 10000000000, colorA: 'black', colorB: 'black', mode: 'subtract' })
  ]
})

let mods = [2,3]
let boxCol = new THREE.Color(rand(currCol))
let dim = 16
let angle = randI(16)*0.25
class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    let col =
    this.shaderMaterial.uniforms.dim = { value: 16 }
    this.shaderMaterial.uniforms.sp = { value: 0.001 }
    this.shaderMaterial.uniforms.mods = { value: mods }
    this.shaderMaterial.uniforms.angle = { value: 0 }
    this.shaderMaterial.uniforms.boxCol = { value: boxCol }
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
        uniform float time;
        uniform float dim;
        uniform float sp;
        uniform float angle;
        uniform float damp;
        uniform sampler2D tOld;
        uniform sampler2D tNew;
        uniform vec2 mods;
        uniform vec3 boxCol;
        varying vec2 vUv;
        vec4 when_gt( vec4 x, float y ) {
            return max( sign( x - y ), 0.000000001 );
        }

        void main() {
            vec2 offset = vec2(0.0, 0.0);
            if (mod(vUv.x * dim, mods.x) < 1.0) {
              if (mod(vUv.y * dim, mods.y) < 1.0) {
                offset = vec2(cos(angle)*sp, sin(angle)*sp);
              }
            } else {
                if (mod(vUv.y * dim, 2.0) >= 1.0) {
                  offset = vec2(-cos(angle)*sp, -sin(angle)*sp);
                }
              }
              
              vec2 uv = vUv + offset;
              vec4 texelOld = texture2D(tOld, uv);
              vec4 texelNew = texture2D(tNew, vUv);
              texelOld *= damp * when_gt( texelOld, 0.000000001 );
              vec4 finalColor = max(texelNew, texelOld);
              
            
              if (mod(vUv.x * dim*0.25, mods.x+2.0) < 1.0 && mod(vUv.y * dim, mods.y+2.0) < 1.0) {
                if (mod(vUv.x * dim*0.25*1.1, mods.x+2.0) > 0.8 && mod(vUv.y * dim, mods.y+2.0) > 0.8) {
                  finalColor = vec4(boxCol,1);
                } 
              } 
                
        
            gl_FragColor = finalColor;
        }

        `
    }
  }
}
let pling = () => {
  fgains.forEach((g)=> {
    g.gain.linearRampToValueAtTime(0.7,audioContext.currentTime+1)
    g.gain.linearRampToValueAtTime(0.01,audioContext.currentTime+3)
  })
} 
extend({ EffectComposer, RenderPass, AfterBurn })
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()
  useFrame(() => {
    ref.current.uniforms.damp.value = damp
    if(randI(300)>298){
      mods = [1+randI(10),1+randI(10)]
      boxCol.set(rand(['black',rand(currCol)]))
      dim= rand([5,6,8,10,12,14,16])
      angle = randI(16)*0.25
      filters.forEach((f) => {
        randI(10)>5 && f.frequency.linearRampToValueAtTime(100+(angle*100),audioContext.currentTime+0.1)
      })
      pling()
    }
    ref.current.uniforms.mods.value = mods
    ref.current.uniforms.boxCol.value = boxCol
    ref.current.uniforms.dim.value = dim
    ref.current.uniforms.angle.value = angle

    damp = 1
    composer.current.render()
  }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} ref={ref} />
    </effectComposer>
  )
}

function App() {
  const size = useWindowSize();
  useEffect(() => {
    damp = 0
  }, [size.height, size.width])
  const start = () => {
    if (!audioStarted) {
      whiteNoise.start(0);
      audioStarted = true;
      // fadeIn()
    } else {
      damp = 0
      dt = 0
    }
    pling()
  }
  return (
    <div id="main" onClick={() => start()} key={size.height + size.width}
      onDoubleClick={() => {
        toggleFullScreen()
      }}
    >
      <Canvas flat={true} linear={true} dpr={[1, 2]} camera={{ position: [0, 0, 2], near: 0.0001, far: 1000, fov: 35 }}
        gl={{ antialias: false }}
      >
        <Thangs />
        <color attach="background" args={[bg]} />
        <OrbitControls />
        <Tint />
      </Canvas>
    </div>
  )
}

ReactDOM.render(
  <App />,
  document.getElementById('root')
)

let c1 = rand(currCol)
let c2 = rand(currCol)
let c3 = rand(currCol)

let oFromCol = new THREE.Color(c3)
let oToCol = new THREE.Color(c3)
let fromCol = [
  new THREE.Color(c1),
  new THREE.Color(c2),
]
let toCol = [
  new THREE.Color(c1),
  new THREE.Color(c2),
]
let dt = 0
let dir = rand([1, -1])
let rS = (3 + randI(7)) * 0.1
let xS = (1 + randI(10)) * 0.01
let sS = (4 + randI(15)) * 0.1
let rads = (3 + randI(14)) * 0.05
let dtMult = 1 + randI(10)
let w = (4 + randI(5)) * 0.1
let bs = 0.001 * rand([2])
let oW = rand([0.15,0.2,0.25])

function Thangs() {
  const group = useRef()
  const oGroup = useRef()
  const left = useRef()
  const right = useRef()
  const top = useRef()
  const bottom = useRef()
  useFrame((s, d) => {
    if (randI(200) > 180) {
      if (randI(10) > 7) {
        let same = rand(currCol)
        c1 = same
        c2 = same
      }
      if (randI(10) > 6) {
        c1 = rand(currCol)
      }
      if (randI(10) > 6) {
        c2 = rand(currCol)
      }
      if (randI(10) > 6) {
        c3 = rand(currCol)
      }
      toCol[0] = toCol[0].set(c1)
      toCol[1] = toCol[1].set(c2)
      oToCol = oToCol.set(c3)
    }
    fromCol[0].set(fromCol[0].lerpColors(fromCol[0], toCol[0], 0.02))
    fromCol[1].set(fromCol[1].lerpColors(fromCol[1], toCol[1], 0.01))
    oFromCol.set(oFromCol.lerpColors(oFromCol, oToCol, 0.1))
    material.layers[0].color.set(fromCol[Math.floor((dt * dtMult) % 2)])
    material2.layers[0].color.set(oFromCol)
    group.current.rotation.z = oGroup.current.rotation.z = (Math.cos(dt * rS) + (dt * 0.1 * dir)) * Math.PI * rads * dir
    dt += 0.01
    group.current.position.x = oGroup.current.position.x = Math.cos(dt * 0.1 * 8) * Math.sin(dt * xS*12)*0.4
    group.current.position.y = oGroup.current.position.y = Math.sin(dt * 0.1 * 8) * Math.cos(dt * xS*18)*0.4
    group.current.scale.y = Math.sin(dt * sS) * w
    left.current.position.y = Math.sin(dt * sS) * w * -oW
    right.current.position.y = Math.sin(dt * sS) * w * oW
    top.current.position.x = Math.sin(dt * sS) * w * oW
    bottom.current.position.x = Math.sin(dt * sS) * w * -oW
    
  })
  return (
    <group position={[0, 0, 0]}>
      <group ref={group}>
        <Instances geometry={new THREE.BoxBufferGeometry(0.003, 0.3, 0.001)} material={material}>
          <Instance />
        </Instances>
      </group>
      <group ref={oGroup}>
        <mesh ref={top} geometry={new THREE.BoxBufferGeometry(bs, bs, bs * 3)} material={material2} />
        <mesh ref={bottom} geometry={new THREE.BoxBufferGeometry(bs, bs, bs * 3)} material={material2} />
        <mesh ref={left} geometry={new THREE.BoxBufferGeometry(bs, bs, bs * 3)} material={material2} />
        <mesh ref={right} geometry={new THREE.BoxBufferGeometry(bs, bs, bs * 3)} material={material2} />
      </group>
    </group>
  )
}

window.AudioContext = window.AudioContext || window.webkitAudioContext;
let audioContext = new AudioContext();
let audioStarted = false;
let bufferSize = 4 * audioContext.sampleRate,
  noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate),
  output = noiseBuffer.getChannelData(0);
for (let i = 0; i < bufferSize; i++) {
  output[i] = Math.random() * 2 - 1;
}

let whiteNoise = audioContext.createBufferSource();
whiteNoise.buffer = noiseBuffer;
whiteNoise.loop = true;

let reverb = SimpleReverb(audioContext);
reverb.connect(audioContext.destination);
reverb.time = 12
reverb.filterType = 'lowpass'
reverb.cutoff.value = 900
// reverb.wet.value = 0.9
reverb.dry.value = 0.97

var getFrequency = function (note) {
  var notes = ['A', 'A#', 'B', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#'],
    octave,
    keyNumber;
  if (note.length === 3) {
    octave = note.charAt(2);
  } else {
    octave = note.charAt(1);
  }
  keyNumber = notes.indexOf(note.slice(0, -1));
  if (keyNumber < 3) {
    keyNumber = keyNumber + 12 + ((octave - 1) * 12) + 1;
  } else {
    keyNumber = keyNumber + ((octave - 1) * 12) + 1;
  }
  return 440 * Math.pow(2, (keyNumber - 49) / 12);
};

let NUM = 6;
let filters = [];
let fgains = [];
let panners = [];

let freqs = []

for (let i = 0; i < NUM; i++) {
  let f = audioContext.createBiquadFilter();
  let p = audioContext.createStereoPanner();
  p.pan.value = (randI(20) * 0.1) - 1;
  panners.push(p)
  f.type = "bandpass";
  f.Q.value = 200;
  let fr = getFrequency(rand(['C', 'D', 'E', 'F', 'G']) + rand([3, 4, 4, 5, 5, 6]))
  freqs.push(fr)
  f.frequency.value = fr
  filters.push(f);
  let g = audioContext.createGain();
  g.gain.value = 0;
  fgains.push(g);
}

for (let i = 0; i < NUM; i++) {
  filters[i].frequency.value = (100+(angle*100))
  whiteNoise.connect(filters[i]);
  filters[i].connect(fgains[i]);
  fgains[i].connect(panners[i]);
  panners[i].connect(reverb);
}

function fadeIn() {
  fgains.map((_, i) => {
    fgains[i].gain.linearRampToValueAtTime(1, audioContext.currentTime + 4)
  })
}

function useWindowSize() { let [e, i] = useState({ width: void 0, height: void 0 }); return useEffect(() => { function e() { i({ width: window.innerWidth, height: window.innerHeight }) } return window.addEventListener("resize", e), e(), () => window.removeEventListener("resize", e) }, []), e }
function toggleFullScreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
  } else if (document.exitFullscreen) {
    document.exitFullscreen();
  }
}

console.log('%c 🍃🍃 @ramioso 2023-02-06 🍃🍃', 'color: green');
